import type { CameraControls } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import { Vector3, type Object3D } from 'three'
import useGame from '../../stores/use-game'

export default function CameraRig() {
  const { controls, size, scene, viewport } = useThree()
  const phase = useGame(state => state.phase)

  const waterTarget = useRef<Object3D>(null!)
  const trainTarget = useRef<Object3D>(null!)

  // Reusable vector to prevent garbage collection allocations in the render loop
  const targetVec = useRef(new Vector3())

  useEffect(() => {
    scene.traverse(child => {
      if (child.userData.name === 'water') waterTarget.current = child
      if (child.userData.name === 'locomotive') trainTarget.current = child
    })
  }, [scene])

  // Handle phase transitions and initial camera setup
  useEffect(() => {
    if (!controls) return
    const cameraControls = controls as CameraControls

    if (phase === 'ready' || phase === 'ended') {
      cameraControls.setTarget(0, 0, 0, true)
      cameraControls.rotatePolarTo(0.5, phase === 'ended')
      cameraControls.dollyTo(3, phase === 'ended')
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#0094a5')
      return
    }

    if (phase === 'started') {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#7cad33')

      if (waterTarget.current) {
        cameraControls.fitToBox(
          waterTarget.current,
          true,
          viewport.aspect < 1 ? { paddingLeft: -1, paddingRight: -1 } : undefined,
        )
        cameraControls.rotatePolarTo(Math.PI * 0.25, true)
      }

      if (viewport.aspect < 1) cameraControls.setTarget(0, -1, 0, true)
    }
  }, [controls, size, viewport.aspect, phase])

  // Continuously follow the train during the 'started' phase
  useFrame(() => {
    if (phase !== 'started' || !trainTarget.current || !controls) return
    const cameraControls = controls as CameraControls

    // Get exact world position (useful if train is nested in groups)
    trainTarget.current.getWorldPosition(targetVec.current)

    // Update target continuously.
    // The last param is 'false' because we don't want internal transition animations
    // when updating every single frame, which would cause stuttering.
    cameraControls.setTarget(targetVec.current.x, targetVec.current.y, targetVec.current.z, false)
  })

  return null
}
